import { createRouter, createWebHistory } from 'vue-router'
import { useStorageManipulation } from './plugins/storage-manipulation.js'
import { useToast } from './plugins/toast/index.js'

const storageManipulation = useStorageManipulation()
const toast = useToast()

const router = new createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/',
            name: 'root',
            // alway redirect to login first
            // routing then decides wether to show login or another site
            beforeEnter: (to, from, next) => {
                next({
                    path: '/login',
                    query: to.query
                })
            }
        },
        {
            path: '/login',
            name: 'login',
            component: () => import('@pages/LoginPage.vue'),
            meta: {
                menu: false,
                menuentry: false,
                display: 'Login'
            },
            beforeEnter: (to, from, next) => {
                // if there is already a session, redirect to dashboard
                if (storageManipulation.getSessionData() !== undefined && !storageManipulation.hasSessionExpired()) {
                    next('/dashboard')
                } else {
                    next()
                }
            }
        },

        // navbar is build in the same order as these entries
        {
            path: '/dashboard',
            name: 'dashboard',
            component: () => import('@pages/DashboardPage.vue'),
            meta: {
                menu: true,
                menuentry: true,
                display: 'menu.dashboard'
            }
        },
        {
            path: '/clientdata',
            name: 'clientdata',
            component: () => import('@pages/ClientDataPage.vue'),
            meta: {
                menu: true,
                menuentry: true,
                display: 'menu.client'
            }
        },
        {
            path: '/contacts',
            name: 'contacts',
            component: () => import('@pages/ContactsPage.vue'),
            meta: {
                menu: true,
                menuentry: true,
                display: 'menu.contacts'
            }
        },
        {
            path: '/contacts/edit',
            name: 'editcontact',
            component: () => import('@pages/EditContactPage.vue'),
            meta: {
                menu: true,
                menuentry: false
            }
        },
        {
            path: '/product-variant-groups',
            name: 'product-variant-groups',
            component: () => import('@pages/ProductVariantGroupsPage.vue'),
            meta: {
                menu: true,
                menuentry: true,
                display: 'menu.productVariantGroups'
            }
        },
        {
            path: '/product-variant-groups/:idProductVariantGroup',
            name: 'product-variant-group',
            component: () => import('@pages/ProductVariantGroupPage.vue'),
            meta: {
                menu: true,
                menuentry: false
            }
        },
        {
            path: '/productconfigurations',
            name: 'productconfigurations',
            component: () => import('@pages/ProductConfigurationsPage.vue'),
            meta: {
                menu: true,
                menuentry: true,
                display: 'menu.products'
            }
        },
        {
            path: '/productconfigurations/edit',
            name: 'editproductconfiguration',
            component: () => import('@pages/EditProductConfigurationPage.vue'),
            meta: {
                menu: true,
                menuentry: false
            }
        },
        {
            path: '/changeoverdays',
            name: 'changeoverdays',
            component: () => import('@pages/ChangeoverDaysPage.vue'),
            meta: {
                menu: true,
                menuentry: true,
                display: 'menu.changeoverdays'
            }
        },
        {
            path: '/changeoverdays/edit',
            name: 'editchangeoverday',
            component: () => import('@pages/EditChangeoverDayPage.vue'),
            meta: {
                menu: true,
                menuentry: false
            }
        },
        {
            path: '/attestations',
            name: 'attestations',
            component: () => import('@pages/AttestationsPage.vue'),
            meta: {
                menu: true,
                menuentry: true,
                display: 'menu.attestations'
            }
        },
        {
            path: '/attestations/edit',
            name: 'editattestation',
            component: () => import('@pages/EditAttestationPage.vue'),
            meta: {
                menu: true,
                menuentry: false
            }
        },
        {
            path: '/links',
            name: 'links',
            component: () => import('@pages/LinksPage.vue'),
            meta: {
                menu: true,
                menuentry: true,
                display: 'menu.links'
            }
        },
        {
            path: '/links/edit',
            name: 'editlink',
            component: () => import('@pages/EditLinkPage.vue'),
            meta: {
                menu: true,
                menuentry: false
            }
        },
        {
            path: '/texts',
            name: 'texts',
            component: () => import('@pages/TextsPage.vue'),
            meta: {
                menu: true,
                menuentry: true,
                display: 'menu.texts'
            }
        },
        {
            path: '/texts/edit',
            name: 'edittext',
            component: () => import('@pages/EditTextPage.vue'),
            meta: {
                menu: true,
                menuentry: false
            }
        },
        {
            path: '/mailtemplates',
            name: 'mailtemplates',
            component: () => import('@pages/MailTemplatesPage.vue'),
            meta: {
                menu: true,
                menuentry: true,
                display: 'menu.mailtemplates'
            }
        },
        {
            path: '/mailtemplates/edit',
            name: 'editmailtemplate',
            component: () => import('@pages/EditMailTemplatePage.vue'),
            meta: {
                menu: true,
                menuentry: false
            }
        },
        {
            path: '/corporations',
            name: 'corporations',
            component: () => import('@pages/CorporationsPage.vue'),
            meta: {
                menu: true,
                menuentry: true,
                display: 'menu.corporations'
            }
        },
        {
            path: '/corporations/edit',
            name: 'editcorporation',
            component: () => import('@pages/EditCorporationPage.vue'),
            meta: {
                menu: true,
                menuentry: false
            },
        },
        {
            path: '/systemconfiguration',
            name: 'systemconfiguration',
            component: () => import('@pages/SystemConfigurationPage.vue'),
            meta: {
                menu: true,
                menuentry: true,
                display: 'menu.systemconfiguration'
            }
        },
        // ----- DEFAULT ROUTE MUST BE LAST! -----
        {
            path: '/:pathMatch(.*)*',
            name: 'notfound',
            component: () => import('@pages/NotFoundPage.vue')
        }
    ],
})

// check if the session is still valid
router.beforeEach((to, from, next) => {
    var sessionData = storageManipulation.getSessionData()

    // always allow navigation to root and login
    // without any comment
    if (to.path == '/' || to.path == '/login') {
        next()
        return
    } else if (sessionData === undefined) {
        next('/login')
    } else {
        // check if the session has ended
        // session timestamp is update everytime a call is made -> main.js axios interceptor
        if (storageManipulation.hasSessionExpired()) {
            storageManipulation.clearSessionData()
            toast.info(this.$t('general.session-expired'))
            next('/login')
        } else {
            next()
        }
    }
})

export default router
